import type { FormApi } from 'final-form';
import ReactDatetime from 'react-datetime';
import {
  Field,
  FieldInputProps,
  FieldProps,
  FieldRenderProps,
  Form,
  FormSpy,
} from 'react-final-form';
import Select from 'react-select';
import { ReportDropdownIndicator, reportDropdownIndicatorStyles } from './reportDropdownIndicator';
import { customRangeOption, dateRangeOptions } from './constants';
import { DateRangeOption, Dates, FormValues } from './types';
import { datesMatch, normalizeDate, truncateToDate, validate } from './utils';

interface ReportOptionsDateSelectorProps extends FieldProps<Dates, FieldRenderProps<Dates>> {
  name: string;
  dateRangeOption: DateRangeOption;
}

export const ReportOptionsDateSelector = ({
  name,
  dateRangeOption = dateRangeOptions[0],
  ...fieldProps
}: ReportOptionsDateSelectorProps) => {
  const handleFormChange = (
    values: FormValues,
    form: FormApi<FormValues>,
    input: FieldInputProps<Dates, HTMLElement>
  ) => {
    const start = truncateToDate(values.startDate);
    const end = truncateToDate(values.endDate);

    if (!start || !end) {
      // Validation enforces both dates; guard remains for type safety.
      return;
    }

    const matchingRange = dateRangeOptions.find(
      option => datesMatch(option.startDate, start) && datesMatch(option.endDate, end)
    );

    if (matchingRange && values.dateRange?.value !== matchingRange.value) {
      form.change('dateRange', matchingRange);
      return;
    }

    if (!matchingRange && values.dateRange?.value !== customRangeOption.value) {
      form.change('dateRange', customRangeOption);
    }

    input.onChange({ start, end });
  };

  return (
    <Field name={name} {...fieldProps}>
      {({ input }) => {
        return (
          <Form<FormValues>
            onSubmit={() => undefined}
            validate={validate}
            initialValues={{
              dateRange: dateRangeOption,
              startDate: dateRangeOption.startDate,
              endDate: dateRangeOption.endDate,
            }}
            render={({ handleSubmit, form }) => (
              <form
                onSubmit={handleSubmit}
                noValidate
                className="report-options-date-selector-body"
              >
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values }) => handleFormChange(values, form, input)}
                />

                <div className="report-options-date-selector-column">
                  <label htmlFor="date-range-select">Filter By</label>
                  <Field
                    name="dateRange"
                    render={({ input, meta }) => (
                      <>
                        <Select
                          inputId="date-range-select"
                          aria-label="Filter by date range"
                          className="react-select"
                          classNamePrefix="react-select"
                          value={(input.value as DateRangeOption | null) || null}
                          onChange={option => {
                            const selected = option as DateRangeOption | null;
                            input.onChange(selected);
                            form.change('startDate', selected?.startDate || null);
                            form.change('endDate', selected?.endDate || null);
                          }}
                          onBlur={input.onBlur}
                          options={dateRangeOptions}
                          components={{
                            DropdownIndicator: ReportDropdownIndicator,
                          }}
                          getOptionLabel={option => option.label}
                          getOptionValue={option => option.value}
                          isClearable={false}
                          styles={reportDropdownIndicatorStyles}
                        />
                        {meta.touched && meta.error && (
                          <span className="text-danger report-options-error-message">
                            {meta.error}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className="report-options-date-selector-column">
                  <label htmlFor="start-date">Start Date</label>
                  <Field
                    name="startDate"
                    render={({ input, meta }) => (
                      <>
                        <ReactDatetime
                          inputProps={{
                            id: 'start-date',
                            placeholder: 'Start Date',
                            'aria-label': 'Start Date',
                            onBlur: input.onBlur,
                          }}
                          value={normalizeDate(input.value)}
                          onChange={value => input.onChange(normalizeDate(value) ?? value)}
                          dateFormat="YYYY-MM-DD"
                          timeFormat={false}
                          closeOnSelect
                        />
                        {meta.touched && meta.error && (
                          <span className="text-danger report-options-error-message">
                            {meta.error}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>

                <div className="report-options-date-selector-column">
                  <label htmlFor="end-date">End Date</label>
                  <Field
                    name="endDate"
                    render={({ input, meta }) => (
                      <>
                        <ReactDatetime
                          inputProps={{
                            id: 'end-date',
                            placeholder: 'End Date',
                            'aria-label': 'End Date',
                            onBlur: input.onBlur,
                          }}
                          value={normalizeDate(input.value)}
                          onChange={value => input.onChange(normalizeDate(value) ?? value)}
                          dateFormat="YYYY-MM-DD"
                          timeFormat={false}
                          closeOnSelect
                        />
                        {meta.touched && meta.error && (
                          <span className="text-danger report-options-error-message">
                            {meta.error}
                          </span>
                        )}
                      </>
                    )}
                  />
                </div>
              </form>
            )}
          />
        );
      }}
    </Field>
  );
};

export default ReportOptionsDateSelector;
